import React, { useState, useEffect } from 'react'
import {
    Box,
    Text,
    Button,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { useIntl, FormattedMessage } from 'react-intl'

/* Styles */
import { useStoreLocatorStyles } from '../styles/storeLocatorStyles'

/* Form fields */
import { set, useForm } from 'react-hook-form'
import StoreLocatorFields from '../../forms/store-locator-fields'

/* Context */
import { useStoreLocator } from '../../../contexts/store-locator-context';

/* Icons */
import { GeneralSearchIcon, Geolocation } from '../../icons'

/* Hooks */
import { useSearchStores } from '@salesforce/commerce-sdk-react'
import { useAddressGeolocation } from '../../../integrations/address-geolocation/hooks/use-address-geolocation'

/* Configs */
import { STORE_LOCATOR_CONFIGS } from '../configs'
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const StoreLocatorInput = () => {
    /* Styling */
    const styles = useStoreLocatorStyles()
    const inputStyling = styles?.components?.inputStyling

    const intl = useIntl()
    const config = getConfig()
    const [showError, setShowError] = useState(false)
    const form = useForm({
        mode: 'onSubmit'
    })

    const {
        stores,
        setStores,
        setSearchValue,
        searchValue,
        getLocation,
        permissionStatus,
        latitude,
        longitude,
        noSearchResults,
        setNoSearchResults,
        setIsStoreDataLoading,
        setLatitude,
        setLongitude,
        setIsGeolocationSearch,
        isGeolocationSearch
    } = useStoreLocator()

    const [searchType, setSearchType] = useState('initial')
    const [hasAttemptedInitialLoad, setHasAttemptedInitialLoad] = useState(false)
    const [offset, setOffset] = useState(0)
    const [address, setAddress] = useState(null)
    const [isFallback, setIsFallback] = useState(false)

    const {
        data: searchStoresData,
        isLoading: isStoreDataLoading
    } = useSearchStores({
        parameters: {
            countryCode: STORE_LOCATOR_CONFIGS.countryCode,
            latitude: latitude,
            longitude: longitude,
            locale: intl.locale,
            ...((permissionStatus === 'granted' || searchValue) && { maxDistance: config.app.STORELOCATOR_SEARCH_RADIUS || STORE_LOCATOR_CONFIGS.STORE_LOCATOR_DISTANCE }),
            limit: STORE_LOCATOR_CONFIGS.NUM_STORES_PER_REQUEST_API_MAX,
            ...(offset !== -1 && { offset: offset }),
            distanceUnit: config.app.STORELOCATOR_DISTANCE_UNIT || STORE_LOCATOR_CONFIGS.STORE_LOCATOR_DISTANCE_UNIT,
            c_brand: 'garage'
        }
    }, {
        enabled: (!!latitude && !!longitude)
    })

    const { location } = useAddressGeolocation(address)

    // Initial load - try geolocation first
    useEffect(() => {
        if (permissionStatus === 'granted') {
            setShowError(false)
        }

        if (!hasAttemptedInitialLoad && permissionStatus === null) {
            setHasAttemptedInitialLoad(true)
            setSearchType('initial')
            getLocation()
        }
    }, [hasAttemptedInitialLoad, permissionStatus, getLocation])

    useEffect(() => {
        if (location) {
            if (!location?.success && STORE_LOCATOR_CONFIGS.isDevelopment) {
                console.error('❌ There is an error on the geolocation API')
            }

            if (location?.success) {
                setSearchType('manual_search')
                setLatitude(location?.lat)
                setLongitude(location?.lng)
                setOffset(-1)
                setSearchValue(address || null)
            } else {
                setSearchType('manual_search_invalid_address')
                setSearchValue(address || null)
            }
        }
    }, [location])

    // Handle geolocation permission status changes
    useEffect(() => {
        if ((permissionStatus !== 'granted' || permissionStatus === 'denied') && searchType === 'initial') {
            if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                console.log('📄 Initial load: Geolocation denied, using default location')
            }
            setLatitude(STORE_LOCATOR_CONFIGS.defaultCityLocation.latitude)
            setLongitude(STORE_LOCATOR_CONFIGS.defaultCityLocation.longitude)
        }
    }, [permissionStatus, searchType])

    const onSubmit = async (data) => {
        if (STORE_LOCATOR_CONFIGS.isDevelopment) {
            console.log('📄 Manual search initiated for:', data?.storeLocatorSearch)
        }

        setAddress(data?.storeLocatorSearch)
        setShowError(false)
    }

    const handleGeolocation = () => {
        if (permissionStatus === 'denied') {
            setShowError(true)
            return
        }

        setShowError(false)
        setSearchValue(null)
        setSearchType('geolocation')
        getLocation()
    }

    useEffect(() => {
        setIsStoreDataLoading(isStoreDataLoading)
    }, [isStoreDataLoading])

    // Hook to populate all stores if the permission was not granted
    useEffect(() => {
        if (searchStoresData && permissionStatus !== 'granted' && offset !== -1) {
            const apiLimit = searchStoresData?.limit
            const apiTotalStores = searchStoresData?.total
            const apiOffset = searchStoresData?.offset

            setStores([...stores, ...searchStoresData?.data])
            setOffset((apiOffset + apiLimit) < apiTotalStores ? apiOffset + apiLimit : apiOffset)

            if ((apiOffset + apiLimit) === apiTotalStores) {
                setOffset(-1)
            }
        }
    }, [searchStoresData, offset])

    // Hook to handle store search results
    useEffect(() => {
        if (!searchStoresData) return

        const hasStores = searchStoresData?.data && searchStoresData.data.length > 0

        switch (searchType) {
            case 'initial':
                if (isGeolocationSearch) {
                    if (hasStores) {
                        if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                            console.log('📄 Initial geolocation: Found stores')
                        }
                        setStores(searchStoresData.data)
                        setNoSearchResults(false)
                        setIsGeolocationSearch(false)
                        setIsFallback(false)
                    } else {
                        if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                            console.log('📄 Initial geolocation: No stores found, falling back to default location')
                        }
                        setSearchType('fallback')
                        setLatitude(STORE_LOCATOR_CONFIGS.defaultCityLocation.latitude)
                        setLongitude(STORE_LOCATOR_CONFIGS.defaultCityLocation.longitude)
                        setIsGeolocationSearch(false)
                        setIsFallback(true)
                    }
                }
                break

            case 'geolocation':
                if (isGeolocationSearch) {
                    if (hasStores) {
                        if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                            console.log('📄 Geolocation button: Found stores')
                        }
                        setStores(searchStoresData.data)
                        setNoSearchResults(false)
                        setIsGeolocationSearch(false)
                        setIsFallback(false)
                    } else {
                        if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                            console.log('📄 Geolocation button: No stores found, falling back to default location')
                        }
                        setSearchType('fallback')
                        setLatitude(STORE_LOCATOR_CONFIGS.defaultCityLocation.latitude)
                        setLongitude(STORE_LOCATOR_CONFIGS.defaultCityLocation.longitude)
                        setIsGeolocationSearch(false)
                        setIsFallback(true)
                    }
                }
                break

            case 'manual_search':
                if (hasStores) {
                    if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                        console.log('📄 Manual search: Found stores')
                    }
                    setStores(searchStoresData.data)
                    setNoSearchResults(false)
                    setIsFallback(false)
                } else {
                    if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                        console.log('📄 Manual search: No stores found, falling back to default location')
                    }
                    setSearchType('fallback')
                    setLatitude(STORE_LOCATOR_CONFIGS.defaultCityLocation.latitude)
                    setLongitude(STORE_LOCATOR_CONFIGS.defaultCityLocation.longitude)
                    setIsFallback(true)
                }
                break
            case 'manual_search_invalid_address':
                if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                    console.log('📄 Manual search invalid address: Invalid address')
                }

                setStores([])
                setNoSearchResults(true)
                setLatitude(null)
                setLongitude(null)
                break
            case 'fallback':
                if (hasStores) {
                    if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                        console.log('📄 Fallback to default location: Found stores')
                    }
                    setStores(searchStoresData.data)
                    setNoSearchResults(true) // Show no results message even if we found stores at fallback location
                    setIsFallback(true)
                } else {
                    if (STORE_LOCATOR_CONFIGS.isDevelopment) {
                        console.log('📄 Fallback to default location: No stores found')
                    }
                    setStores([])
                    setNoSearchResults(true)
                    setIsFallback(true)
                }
                // Reset search type after processing fallback
                setSearchType('completed')
                break

            default:
                // For any other cases or completed state, just update stores if needed
                if (hasStores && stores.length === 0) {
                    setStores(searchStoresData.data)
                    setNoSearchResults(false)
                }
                break
        }
    }, [searchStoresData, searchType, isGeolocationSearch, permissionStatus])

    return (
        <Box>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                <Box {...styles.inputWrapper}>
                    <Box {...inputStyling.container}>
                        <StoreLocatorFields form={form} {...inputStyling.inputField} />
                        <GeneralSearchIcon {...inputStyling.icon} />
                    </Box>
                    <Box {...inputStyling.results}>
                        {searchValue ? (
                            <Text {...inputStyling.stores}>
                                {isFallback &&
                                    <FormattedMessage
                                        id='store_locator.input.store_near'
                                        defaultMessage='{noOfStores} store near "{searchValue}"'
                                        values={{
                                            noOfStores: noSearchResults ? 0 : (stores?.length || 0),
                                            // data-private: address/postcode search keywords are PII for LogRocket
                                            searchValue: <Text as="span" data-private>{searchValue}</Text>
                                        }}
                                    />}
                                {stores?.length > 0 && !isFallback &&
                                    <FormattedMessage
                                        id='store_locator.input.stores_near'
                                        defaultMessage='{noOfStores} stores near "{searchValue}"'
                                        values={{
                                            noOfStores: stores?.length,
                                            // data-private: address/postcode search keywords are PII for LogRocket
                                            searchValue: <Text as="span" data-private>{searchValue}</Text>
                                        }}
                                    />}
                                {stores?.length === 0 && !isFallback &&
                                    <FormattedMessage
                                        id='store_locator.input.stores_near'
                                        defaultMessage='{noOfStores} stores near "{searchValue}"'
                                        values={{
                                            noOfStores: stores?.length,
                                            // data-private: address/postcode search keywords are PII for LogRocket
                                            searchValue: <Text as="span" data-private>{searchValue}</Text>
                                        }}
                                    />}
                            </Text>
                        ) : (
                            <Text {...inputStyling.stores}>
                                <FormattedMessage
                                    id='store_locator.input.stores'
                                    defaultMessage="{noOfStores} stores"
                                    values={{ noOfStores: noSearchResults ? 0 : (stores?.length || 0) || 0 }}
                                />
                            </Text>
                        )}
                        <Button as={Link}
                            variant={'unstyled'}
                            href={'#'}
                            {...inputStyling.geolocation}
                            onClick={handleGeolocation}
                            onKeyDown={(e) => {
                                if (e && e.key === 'Enter') {
                                    e.preventDefault()
                                    handleGeolocation(e)
                                }
                            }}>
                            <Geolocation {...inputStyling.geoIcon} />
                            <FormattedMessage
                                id='store_locator.input.geolocation'
                                defaultMessage="Use Current Location"
                            />
                        </Button>
                    </Box>
                    {showError &&
                        <Text {...inputStyling.geoError}>
                            <FormattedMessage
                                id='store_locator.input.geo_error'
                                defaultMessage="Looks like you've blocked location access. Please enable it in your browser settings."
                            />
                        </Text>
                    }
                </Box>
            </form>
        </Box>
    )
}

export default StoreLocatorInput
